import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const RegexGuide = () => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>Common Tokens</Typography>
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <Typography variant="caption">`\d` - Digit</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption">`\w` - Word character</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption">`\s` - Whitespace</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption">`.` - Any character</Typography>
      </Grid>
    </Grid>
    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Quantifiers</Typography>
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <Typography variant="caption">`*` - 0 or more</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption">`+` - 1 or more</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption">`?` - 0 or 1</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption">`{'{n}'}` - Exactly n times</Typography>
      </Grid>
    </Grid>
  </Box>
);

function RegexTester() {
  const [regex, setRegex] = useState('');
  const [testString, setTestString] = useState('');
  const [flags, setFlags] = useState({
    g: true,
    i: false,
    m: false,
  });
  const [showGuide, setShowGuide] = useState(false);

  const handleFlagChange = (event) => {
    setFlags({ ...flags, [event.target.name]: event.target.checked });
  };

  const highlightedText = useMemo(() => {
    if (!regex) {
      return testString;
    }
    try {
      const flagString = Object.keys(flags).filter(key => flags[key]).join('');
      const re = new RegExp(regex, flagString);
      return testString.replace(re, (match) => `<mark>${match}</mark>`);
    } catch (error) {
      return testString;
    }
  }, [regex, testString, flags]);

  const matches = useMemo(() => {
    if (!regex) {
      return [];
    }
    try {
      const flagString = Object.keys(flags).filter(key => flags[key]).join('');
      const re = new RegExp(regex, flagString);
      const allMatches = [];
      let match;
      while ((match = re.exec(testString)) !== null) {
        allMatches.push(match);
      }
      return allMatches;
    } catch (error) {
      return [{
        error: true,
        message: error.message
      }];
    }
  }, [regex, testString, flags]);

  return (
    <>
      <SEO
        title="Regex Tester & Explainer | Online Regular Expressions | UmbrellaCo"
        description="Test, debug, and explain regular expressions online. Real-time highlighting, match extraction, and visual regex guide for developers."
        keywords="regex tester, regular expression online, regex explainer, regex debugger, test regex"
        url="/dev-tools/regex-tester"
        jsonLd={createSoftwareAppSchema(
          'Regex Tester & Explainer',
          'Test and debug regular expressions with real-time highlighting.',
          'https://umbrellaco.in/dev-tools/regex-tester',
          ['Regular Expressions', 'Developer Tools', 'Text Parsing']
        )}
      />
      <ToolPageContainer
        title="Regex Tester & Explainer"
        description="Write, test, and debug regular expressions with real-time match highlighting and capturing group extraction."
        disablePadding
      >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
        <Tooltip title="Regex Quick Reference">
          <IconButton onClick={() => setShowGuide(!showGuide)}>
            <HelpOutline />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={showGuide}>
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          <RegexGuide />
        </Paper>
      </Collapse>

      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <TextField
              label="Regular Expression"
              value={regex}
              onChange={(e) => setRegex(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ mb: 1 }}
            />
            <Box>
              <FormControlLabel control={<Checkbox checked={flags.g} onChange={handleFlagChange} name="g" />} label="Global (g)" />
              <FormControlLabel control={<Checkbox checked={flags.i} onChange={handleFlagChange} name="i" />} label="Case-insensitive (i)" />
              <FormControlLabel control={<Checkbox checked={flags.m} onChange={handleFlagChange} name="m" />} label="Multiline (m)" />
            </Box>
            <TextField
              label="Test String"
              value={testString}
              onChange={(e) => setTestString(e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              rows={10}
              sx={{ flexGrow: 1, mt: 1 }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Paper sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Typography variant="h6">Result</Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, flexGrow: 1, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 1 }}
                dangerouslySetInnerHTML={{ __html: highlightedText }}
              />
            </Box>
            <Box sx={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', minHeight: 0, mt: 2 }}>
              <Typography variant="h6">Matches</Typography>
              <Paper variant="outlined" sx={{ p: 2, flexGrow: 1, overflow: 'auto', mt: 1 }}>
                {matches.map((match, index) => (
                  match.error ?
                  <Typography key={index} color="error">{match.message}</Typography> :
                  <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>Match {index + 1}:</strong> {match[0]}</Typography>
                    {match.length > 1 && (
                      <Box sx={{ pl: 2 }}>
                        {match.slice(1).map((group, i) => (
                          <Typography key={i} variant="caption">Group {i + 1}: {group || 'undefined'}</Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      </ToolPageContainer>
    </>
  );
}

export default RegexTester;